import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Pagination, Table, Tag, Typography } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  RightOutlined
} from '@ant-design/icons';
import style from './index.scss';
import {
  conditionMethodTags,
  formatThresholdValue,
  groupCombinationsByResult,
  keyedConditions
} from './thresholdFormat';

const { Text, Paragraph } = Typography;

const SUMMARY_SIZE = 3;
const PAGE_SIZE = 5;
const RESULT_WIDTH = 300;
// Every condition keeps a column; past what fits, the table scrolls sideways with Result pinned.
const CONDITION_WIDTH = 200;

const formatPct = (value) =>
  value == null ? '' : `${(value * 100).toFixed(1)}%`;
const isPercentileLabel = (label) => /^p\d+$/.test(label);
const thresholdsByKey = (thresholds) =>
  keyedConditions(thresholds).reduce(
    (acc, { key, threshold }) => ({ ...acc, [key]: threshold }),
    {}
  );
const groupKey = (group) => `result-${group.flaggedCount}`;
const plural = (n, noun) => `${n} ${noun}${n === 1 ? '' : 's'}`;

const ResultSummary = ({ group, closest }) => {
  const delta = group.alertDeltaPct;
  return (
    <span className={style.resultSummary}>
      <span>
        <Text strong>{plural(group.flaggedCount, 'alert')}</Text>{' '}
        <Text type="secondary">· {formatPct(group.flaggedPct)}</Text>
        {closest && <span className={style.closestTag}>closest to target</span>}
      </span>
      <span className={style.resultMeta}>
        {delta != null && delta !== 0 && (
          <span
            className={delta > 0 ? style.resultDeltaUp : style.resultDeltaDown}>
            {delta > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{' '}
            {delta > 0 ? '+' : ''}
            {delta.toFixed(1)}% vs generation time
          </span>
        )}
        <Text type="secondary">
          {plural(group.combinations.length, 'combination')}
        </Text>
      </span>
    </span>
  );
};

const ConditionCell = ({ threshold, differs }) =>
  threshold ? (
    <div className={differs ? style.conditionDiffers : style.condition}>
      <div className={style.conditionValue}>
        {formatThresholdValue(threshold.value)}
      </div>
      {conditionMethodTags(threshold).map((label) => (
        <Tag
          key={label}
          color={isPercentileLabel(label) ? undefined : 'geekblue'}>
          {label}
        </Tag>
      ))}
    </div>
  ) : null;

const groupShape = PropTypes.shape({
  flaggedCount: PropTypes.number,
  flaggedPct: PropTypes.number,
  alertDeltaPct: PropTypes.number,
  combinations: PropTypes.array
});

ResultSummary.propTypes = {
  group: groupShape.isRequired,
  closest: PropTypes.bool
};

ConditionCell.propTypes = {
  threshold: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  differs: PropTypes.bool
};

/**
 * FIN-13987 (Option E) — Threshold Combinations, the same view for single- and multi-condition
 * rules. The drawer is 720px, too narrow for a column per condition, so it shows the top results
 * only; the full grouped table opens in a wide modal. View-only: users type the values into the
 * rule themselves.
 */
const CombinationSelector = ({
  combinations = [],
  targetAlertRate,
  currentThresholds,
  currentFlaggedCount,
  totalAfterFixedFilter
}) => {
  const hasTarget = targetAlertRate != null;
  const groups = useMemo(
    () => groupCombinationsByResult(combinations, targetAlertRate),
    [combinations, targetAlertRate]
  );
  // One column per condition, matched by identity: every generation-time condition (one the
  // analyser found no candidate for is missing from the combinations), then any it doesn't list.
  const baselineConditions = keyedConditions(currentThresholds);
  const baselineKeys = baselineConditions.map((c) => c.key);
  const conditions = [
    ...baselineConditions,
    ...keyedConditions(combinations[0] && combinations[0].thresholds).filter(
      (c) => !baselineKeys.includes(c.key)
    )
  ];

  const baselineText =
    currentFlaggedCount == null
      ? ''
      : `${plural(currentFlaggedCount, 'alert')}${
          totalAfterFixedFilter
            ? ` · ${formatPct(currentFlaggedCount / totalAfterFixedFilter)}`
            : ''
        }`;

  const [view, setView] = useState({ open: false, page: 1, expanded: [] });
  const openAt = (index) =>
    setView({
      open: true,
      page: Math.floor(index / PAGE_SIZE) + 1,
      expanded: [groupKey(groups[index])]
    });

  // Option numbers run across every page, in display order.
  const rows = useMemo(() => {
    let option = 0;
    return groups.map((group) => ({
      key: groupKey(group),
      group,
      children: group.combinations.map((combo) => {
        option += 1;
        return {
          key: `combination-${combo.combinationId}`,
          name: `Option ${option}`,
          values: thresholdsByKey(combo.thresholds),
          differs: group.differs
        };
      })
    }));
  }, [groups]);

  const pageRows = rows.slice(
    (view.page - 1) * PAGE_SIZE,
    view.page * PAGE_SIZE
  );
  const dataSource = baselineConditions.length
    ? [
        {
          key: 'baseline',
          baseline: true,
          values: thresholdsByKey(currentThresholds)
        },
        ...pageRows
      ]
    : pageRows;

  const columns = [
    {
      title: 'Result',
      key: 'result',
      fixed: 'left',
      width: RESULT_WIDTH,
      render: (_, row) => {
        if (row.baseline) {
          return (
            <span className={style.resultSummary}>
              <Text strong>At generation time</Text>
              <Text type="secondary">{baselineText}</Text>
            </span>
          );
        }
        if (row.group) {
          return (
            <ResultSummary
              group={row.group}
              closest={hasTarget && row.group === groups[0]}
            />
          );
        }
        return row.name;
      }
    },
    ...conditions.map(({ key, label }) => ({
      key,
      width: CONDITION_WIDTH,
      title: (
        <span className={style.conditionHeader} title={label}>
          {label}
        </span>
      ),
      render: (_, row) => {
        if (row.group) return null;
        if (!row.values[key]) {
          return row.baseline ? null : (
            <Text type="secondary">No candidate</Text>
          );
        }
        return (
          <ConditionCell
            threshold={row.values[key]}
            differs={!row.baseline && row.differs.includes(key)}
          />
        );
      }
    }))
  ];

  if (!combinations || combinations.length === 0) {
    return null;
  }

  return (
    <div className={style.combinationSelector}>
      <Text strong className={style.label}>
        Threshold Combinations
      </Text>
      {hasTarget && (
        <Text type="secondary" className={style.targetRate}>
          Target alert rate: {formatPct(targetAlertRate)}
        </Text>
      )}
      {baselineText && (
        <Text type="secondary" className={style.targetRate}>
          At generation time: {baselineText}
        </Text>
      )}
      <div className={style.summaryList}>
        {groups.slice(0, SUMMARY_SIZE).map((group, i) => (
          <button
            type="button"
            key={groupKey(group)}
            className={style.summaryRow}
            onClick={() => openAt(i)}>
            <ResultSummary group={group} closest={hasTarget && i === 0} />
            <RightOutlined className={style.summaryChevron} />
          </button>
        ))}
      </div>
      <div className={style.summaryFooter}>
        <Button onClick={() => openAt(0)}>View all combinations</Button>
        {groups.length > SUMMARY_SIZE && (
          <Text type="secondary">
            Top {SUMMARY_SIZE} of {groups.length} results
          </Text>
        )}
      </div>

      <Modal
        visible={view.open}
        title={
          <span>
            All threshold combinations{' '}
            <Text type="secondary" className={style.modalSubtitle}>
              {plural(conditions.length, 'condition')} ·{' '}
              {hasTarget
                ? `closest to the ${formatPct(targetAlertRate)} target first`
                : 'most alerts first'}
            </Text>
          </span>
        }
        width={1200}
        footer={null}
        onCancel={() => setView((v) => ({ ...v, open: false }))}>
        <Paragraph type="secondary" className={style.legend}>
          <Text strong>pNN</Text> is that percentile of the analysed
          transactions (p50 is the median), <Text strong>Mean</Text> is the
          average. A value with several labels came out the same for each of
          them. <span className={style.legendDiffers}>Highlighted</span> values
          differ between the combinations of one result.
        </Paragraph>
        <Table
          size="small"
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          scroll={{ x: RESULT_WIDTH + conditions.length * CONDITION_WIDTH }}
          rowClassName={(row) => (row.baseline ? style.baselineRow : '')}
          expandable={{
            expandedRowKeys: view.expanded,
            onExpandedRowsChange: (keys) =>
              setView((v) => ({ ...v, expanded: keys }))
          }}
        />
        <Pagination
          className={style.resultPager}
          size="small"
          current={view.page}
          pageSize={PAGE_SIZE}
          total={groups.length}
          hideOnSinglePage
          showSizeChanger={false}
          showTotal={(total, [from, to]) => `Results ${from}–${to} of ${total}`}
          onChange={(page) => setView((v) => ({ ...v, page }))}
        />
      </Modal>
    </div>
  );
};

CombinationSelector.propTypes = {
  combinations: PropTypes.arrayOf(
    PropTypes.shape({
      thresholds: PropTypes.oneOfType([PropTypes.array, PropTypes.string])
    })
  ),
  targetAlertRate: PropTypes.number,
  currentThresholds: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  currentFlaggedCount: PropTypes.number,
  totalAfterFixedFilter: PropTypes.number
};

export default CombinationSelector;
